// Library methods
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import SearchBox from "src/components/shared/inputs/SearchBox/SearchBox";
import { BuildingIcon, ChevronDownIcon } from "src/assets/icons";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type TempClinicType = {
  id?: string;
  name?: string;
  timezone?: string;
};

const ClinicSwitcher = () => {
  const navigate = useNavigate();

  const [menu, setMenu] = useState<HTMLButtonElement | null>(null);
  const [tempClinic, setTempClinic] = useState<TempClinicType>({});

  const { clinicInfo, allClinicInfo, setClinicId } = useClinicContext();

  // clicking the switcher button
  const menuClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setMenu(event.currentTarget);

  // closing the menu
  const menuClose = () => setMenu(null);

  // update the selected clinic whenever the user picks one
  useEffect(() => {
    if (!tempClinic?.id) return;

    menuClose();
    setClinicId(tempClinic.id);
    navigate("/");
  }, [tempClinic, setClinicId, navigate]);

  // reset tempClinic when the menu closes
  useEffect(() => {
    if (!menu) setTempClinic({});
  }, [menu]);

  // close the menu on window resize
  useEffect(() => {
    const handleResize = () => menuClose();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!allClinicInfo || !allClinicInfo?.length) {
    return <></>;
  }

  return (
    <>
      <Button
        onClick={menuClick}
        disabled={allClinicInfo.length <= 1}
        sx={{
          display: "flex",
          minWidth: { xs: 0, md: "fit-content" },
          color: "#FFF",

          ":disabled": {
            color: "#FFF",
          },
        }}
      >
        <Box display="flex" alignItems="center">
          <Box sx={{ marginRight: "8px", fontSize: "20px" }}>
            <BuildingIcon />
          </Box>
          <Typography
            variant="button"
            sx={{
              maxWidth: { xs: 0, md: "200px" },
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: { xs: "none", md: "block" },
              textTransform: "capitalize",
              fontSize: "16px",
              borderBottom:
                menu !== null ? "2px solid white" : "2px solid transparent",
            }}
          >
            {clinicInfo?.name}
          </Typography>
          {allClinicInfo?.length > 1 && (
            <Box
              sx={{
                marginLeft: "8px",
                fontSize: "20px",
                display: { xs: "none", md: "block" },
              }}
            >
              <ChevronDownIcon />
            </Box>
          )}
        </Box>
      </Button>

      <Popover
        sx={{ mt: "16px" }}
        open={Boolean(menu)}
        anchorEl={menu}
        onClose={menuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          style: {
            width: window.innerWidth > 600 ? "280px" : "100%",
          },
        }}
      >
        <Grid container display="flex" justifyContent="center">
          <SearchBox
            id="clinic-switcher-search-box"
            data={allClinicInfo}
            state={clinicInfo}
            setState={setTempClinic}
            maxHeight="40vh"
            defaultShowList={true}
            parentOpen={Boolean(menu)}
          />
        </Grid>
      </Popover>
    </>
  );
};

export default ClinicSwitcher;
